import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { Settings, ArrowLeft, Save, CheckSquare, Square } from 'lucide-react';

const ServicePermissionConfig = ({ onBack }) => {
  const [services, setServices] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [servicePermissions, setServicePermissions] = useState([]); // IDs of permissions for selected service
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesData, permissionsData] = await Promise.all([
        apiService.getAdminServices(),
        apiService.getAdminPermissions()
      ]);
      setServices(servicesData);
      setPermissions(permissionsData);
      if (servicesData.length > 0) {
        setSelectedServiceId(servicesData[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedServiceId) {
      fetchServicePermissions(selectedServiceId);
    }
  }, [selectedServiceId]);

  const fetchServicePermissions = async (serviceId) => {
    try {
      const data = await apiService.getServicePermissions(serviceId);
      setServicePermissions(data.map(p => p.id));
    } catch (error) {
      console.error("Failed to fetch service permissions", error);
    }
  };

  const handleTogglePermission = (permissionId) => {
    setServicePermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedServiceId) return;
    setSaving(true);
    try {
      await apiService.updateServicePermissions(selectedServiceId, servicePermissions);
      alert('保存成功');
    } catch (error) {
      console.error("Failed to save service permissions", error);
      alert('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(perm);
    return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-md overflow-hidden min-h-[500px]">
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center">
          <button 
            onClick={onBack} 
            className="mr-4 text-gray-600 hover:text-indigo-600 flex items-center transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            仪表盘
          </button>
          <h2 className="text-xl font-semibold text-gray-800">权限-服务配置管理</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !selectedServiceId}
          className={`bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center shadow-sm transition-colors ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? '保存中...' : '保存配置'}
        </button>
      </div>

      <div className="flex h-[600px]">
        {/* Left Sidebar: Services List */}
        <div className="w-1/4 border-r border-gray-200 bg-gray-50 overflow-y-auto">
          <div className="p-4 font-medium text-gray-500 uppercase text-xs tracking-wider">选择服务</div>
          <ul>
            {services.map(service => (
              <li key={service.id}>
                <button
                  onClick={() => setSelectedServiceId(service.id)}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-l-4 ${
                    selectedServiceId === service.id 
                      ? 'bg-white border-indigo-600 text-indigo-600 shadow-sm' 
                      : 'border-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  {service.name}
                  <div className="text-xs text-gray-400 font-normal mt-1">{service.code}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Right Content: Permissions Grid */}
        <div className="w-3/4 p-6 overflow-y-auto bg-white">
          {loading ? (
            <div className="text-center py-10 text-gray-500">加载中...</div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedPermissions).map(([category, perms]) => (
                <div key={category}>
                  <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2 flex items-center">
                    <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded mr-2 uppercase">{category}</span>
                    权限组
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {perms.map(perm => {
                      const isSelected = servicePermissions.includes(perm.id);
                      return (
                        <div 
                          key={perm.id}
                          onClick={() => handleTogglePermission(perm.id)}
                          className={`
                            relative border rounded-lg p-4 cursor-pointer transition-all duration-200
                            ${isSelected 
                              ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' 
                              : 'border-gray-200 hover:border-indigo-300 hover:shadow-sm'}
                          `}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className={`font-medium text-sm ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
                                {perm.name}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1 font-mono">{perm.code}</p>
                              {perm.description && (
                                <p className="text-xs text-gray-400 mt-2 line-clamp-2">{perm.description}</p>
                              )}
                            </div>
                            <div className={`ml-3 flex-shrink-0 ${isSelected ? 'text-indigo-600' : 'text-gray-300'}`}>
                              {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicePermissionConfig;